import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import api from '../../services/api';
import { toast } from 'react-toastify';
import { Form, Spinner, Image, Button, CloseButton, Row, Col } from 'react-bootstrap';

const AddProjectLayer = () => {
  // ... (Keep all existing state variables: imagePreviewUrl, masterplanPreviewUrl, locations, etc.)
  const [imagePreviewUrl, setImagePreviewUrl] = useState('');
  const [masterplanPreviewUrl, setMasterplanPreviewUrl] = useState('');
  const [locations, setLocations] = useState([]);
  const [developers, setDevelopers] = useState([]);
  const [user, setUser] = useState(null);
  const [paymentplans, setPaymentPlans] = useState([]);
  const [hasIncentive, setHasIncentive] = useState(false); // Default to false
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: '',
    picture: '',
    masterplan: '',
    startingPrice: '',
    location_id: '',
    developer_id: '',
    paymentPlans: [{ paymentplan_id: '', offerPaymentplan_id: null }], // Changed to array of payment plans
    projectCommission: '',
    deliveryDate: '',
    isLaunch: false,
    isSponsored: false,
    gallery: [],
    // Initialize incentive fields as empty
    incentive: '',
    incentiveStartDate: '',
    incentiveEndDate: '',
    incentiveType: '',
    nameOfAccount: '',
    bankName: '',
    iban: '',
  });

  // ... (Keep existing useEffect hooks for fetching data and setting developer_id) ...
  useEffect(() => {
    fetchLocations();
    fetchPaymentPlans();
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
  }, []);

  useEffect(() => {
    if (user?.type !== 'admin') {
      // Admin doesn't select developer, it's tied to their account implicitly
      // Or if you want non-admins to *not* have a developer_id sent at all:
      setFormData(prev => ({ ...prev, developer_id: '' })); // Keep developer_id field but clear it if needed
      // If developer_id should ONLY exist for admins, you might handle this during submission instead
    }
  }, [user]);

  useEffect(() => {
    if (user?.type === 'admin') {
      fetchDevelopers();
    }
  }, [user]);


  // --- Data Fetching Functions (Keep existing fetchLocations, fetchPaymentPlans, fetchDevelopers) ---
  const fetchLocations = async () => {
    try {
      const endpoint = '/location/unpaginated';
      const { data } = await api.get(endpoint);
      if (data.status && data.response?.data) {
        setLocations(data.response.data);
      } else {
        console.error('Error fetching locations:', data.message || 'No data found');
        toast.error("Could not fetch locations.");
      }
    } catch (error) {
      console.error('Error fetching locations:', error);
      toast.error("An error occurred while fetching locations.");
    }
  };
  const fetchPaymentPlans = async () => {
    try {
      const endpoint = '/paymentplans/unpaginated';
      const { data } = await api.get(endpoint);
      if (data.status && data.response?.data) {
        setPaymentPlans(data.response.data);
      } else {
        console.error('Error fetching payment plans:', data.message || 'No data found');
        toast.error("Could not fetch payment plans.");
      }
    } catch (error) {
      console.error('Error fetching payment plans:', error);
      toast.error("An error occurred while fetching payment plans.");
    }
  };
  const fetchDevelopers = async () => {
    if (user?.type !== 'admin') return;
    try {
      const endpoint = '/developers/unpaginated';
      const { data } = await api.get(endpoint);
      if (data.status && data.response?.data) {
        setDevelopers(data.response.data);
      } else {
        console.error('Error fetching developers:', data.message || 'No data found');
        toast.error("Could not fetch developers.");
      }
    } catch (error) {
      console.error('Error fetching developers:', error);
      toast.error("An error occurred while fetching developers.");
    }
  };

  // --- Transform data for react-select (Keep existing useMemo for locationOptions) ---
  const locationOptions = useMemo(() => {
    return locations.map(loc => ({
      value: loc.id,
      label: loc.name
    }));
  }, [locations]);

  // --- Event Handlers ---

  // ... (Keep existing handleStartingPriceChange ) ...
  const handleStartingPriceChange = (e) => {
    const { value } = e.target;
    const numericValue = value.replace(/[^0-9]/g, "");
    const formattedValue = Number(numericValue).toLocaleString();
    setFormData((prevState) => ({
      ...prevState,
      startingPrice: formattedValue,
    }));
  };

  // *** MODIFIED: Handle multiple files change for Gallery ***
  const handleMultipleFilesChange = async (event, fieldName) => {
    const files = Array.from(event.target.files);
    const maxSize = 2 * 1024 * 1024; // 2MB
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    const maxFiles = 6; // <-- Define the max number of files
    let successfulUploads = [];
    let failedCount = 0;

    // --- ADDED: Check maximum number of files ---
    // Calculate how many more files can be added
    const currentFileCount = formData[fieldName]?.length || 0;
    const remainingSlots = maxFiles - currentFileCount;

    if (files.length > remainingSlots) {
      toast.error(`You can only add ${remainingSlots > 0 ? `${remainingSlots} more image(s)` : 'no more images'}. Maximum ${maxFiles} images allowed.`);
      event.target.value = null; // Clear the selection
      return; // Stop processing
    }
    // --- END ADDED CHECK ---


    const processFile = (file) => {
      return new Promise((resolve, reject) => {
        if (!allowedTypes.includes(file.type)) {
          toast.error(`Invalid file type: ${file.name}. Only JPG, JPEG, PNG allowed.`);
          return reject("Invalid file type");
        }
        if (file.size > maxSize) {
          toast.error(`File ${file.name} exceeds the 2MB size limit.`);
          return reject("File size too large");
        }
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = (error) => {
          console.error("FileReader error: ", error);
          toast.error(`Error reading file ${file.name}.`);
          reject(error);
        };
        reader.readAsDataURL(file);
      });
    };

    const results = await Promise.allSettled(files.map(file => processFile(file)));
    results.forEach(result => {
      if (result.status === 'fulfilled') {
        successfulUploads.push(result.value);
      } else {
        failedCount++;
        console.error("Failed to process file:", result.reason);
      }
    });

    if (successfulUploads.length > 0) {
      setFormData((prevData) => ({
        ...prevData,
        [fieldName]: [...(prevData[fieldName] || []), ...successfulUploads], // Append new valid files
      }));
    }

    event.target.value = null; // Clear input after processing attempt

    if (failedCount > 0) toast.warning(`${failedCount} file(s) could not be added due to size or type.`);
    // Show warning if user selected files but none were valid
    if (successfulUploads.length === 0 && files.length > 0) {
      toast.warning("No valid gallery files were added from the selection.");
    }
  };

  // ... (Keep existing handleFileChange, handleChange, handleSelectChange ) ...
  const handleFileChange = (e, fieldName) => {
    const file = e.target.files[0];
    const maxSize = 2 * 1024 * 1024; // 2MB
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    const allowedTypesMsg = 'JPG, JPEG, PNG';

    if (!file) {
      setFormData({ ...formData, [fieldName]: '' });
      if (fieldName === 'picture') setImagePreviewUrl('');
      if (fieldName === 'masterplan') setMasterplanPreviewUrl('');
      e.target.value = null;
      return;
    }

    if (!allowedTypes.includes(file.type)) {
      toast.error(`Invalid file type for ${fieldName}. Allowed: ${allowedTypesMsg}`);
      e.target.value = null;
      setFormData({ ...formData, [fieldName]: '' });
      if (fieldName === 'picture') setImagePreviewUrl('');
      if (fieldName === 'masterplan') setMasterplanPreviewUrl('');
      return;
    }
    if (file.size > maxSize) {
      toast.error(`File for ${fieldName} exceeds 2MB.`);
      e.target.value = null;
      setFormData({ ...formData, [fieldName]: '' });
      if (fieldName === 'picture') setImagePreviewUrl('');
      if (fieldName === 'masterplan') setMasterplanPreviewUrl('');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64Result = reader.result;
      setFormData({ ...formData, [fieldName]: base64Result });
      if (fieldName === 'picture') setImagePreviewUrl(base64Result);
      if (fieldName === 'masterplan') setMasterplanPreviewUrl(base64Result);
    };
    reader.onerror = (error) => {
      console.error("FileReader error: ", error);
      toast.error(`Error reading file ${file.name}.`);
      setFormData({ ...formData, [fieldName]: '' });
      if (fieldName === 'picture') setImagePreviewUrl('');
      if (fieldName === 'masterplan') setMasterplanPreviewUrl('');
      e.target.value = null;
    };
    reader.readAsDataURL(file);
  };

  const handleChange = (e) => {
    const { id, name, value, type, checked } = e.target;
    const key = type === 'radio' ? name : (id || name);
    let newValue;

    if (type === 'checkbox' || type === 'switch') {
      newValue = checked;
    } else if (type === 'radio') {
      // Special handling for radio groups like 'hasIncentive' or 'incentiveType'
      // These are handled by their specific handlers or fall through correctly
      newValue = value;
    } else {
      newValue = value;
    }

    // Update the state
    setFormData((prevState) => ({ ...prevState, [key]: newValue }));

    // Clear bank details if isLaunch is unchecked
    if (key === 'isLaunch' && !checked) {
      setFormData((prevState) => ({
        ...prevState,
        nameOfAccount: '',
        bankName: '',
        iban: '',
      }));
    }
  };

  const handleSelectChange = (selectedOption, actionMeta) => {
    const { name } = actionMeta;
    setFormData((prevState) => ({
      ...prevState,
      [name]: selectedOption ? selectedOption.value : '',
    }));
  };


  // *** REVISED: handleHasIncentiveChange - Ensure state and formData are clean ***
  const handleHasIncentiveChange = (e) => {
    const value = e.target.value === 'yes'; // Convert 'yes'/'no' string to boolean
    setHasIncentive(value);

    // Explicitly clear incentive fields in formData if "No" is selected
    if (!value) {
      setFormData(prev => ({
        ...prev,
        incentive: '',         // Clear the value
        incentiveType: '',     // Clear the type
        incentiveStartDate: '', // Clear the start date
        incentiveEndDate: '',   // Clear the end date
      }));
    }
    // If 'Yes' is selected, the fields will be populated by user input via the regular handleChange
  };


  // ... (Keep existing handleRemoveGalleryImage, handleCancel) ...
  const handleRemoveGalleryImage = (indexToRemove) => {
    setFormData(prevData => ({
      ...prevData,
      gallery: prevData.gallery.filter((_, index) => index !== indexToRemove),
    }));
  };

  const handleCancel = () => {
    navigate(-1);
  };


  // *** REVISED: handleSubmit - More explicit data preparation ***
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // --- Frontend Validation ---
    let isValid = true;
    const requiredFields = ['name', 'picture', 'masterplan', 'startingPrice', 'location_id', 'projectCommission', 'deliveryDate'];
    if (user?.type === 'admin') {
      requiredFields.push('developer_id');
    }

    requiredFields.forEach(field => {
      const value = formData[field];
      if (typeof value === 'string' && !value.trim()) {
        toast.error(`Field "${field.replace('_id', '')}" is required.`);
        isValid = false;
      } else if (!value && typeof value !== 'string') {
        if (field !== 'developer_id' || user?.type === 'admin') {
          toast.error(`Field "${field.replace('_id', '')}" is required.`);
          isValid = false;
        }
      }
    });

    // Validate payment plans
    if (!formData.paymentPlans || formData.paymentPlans.length === 0) {
      toast.error("At least one payment plan is required.");
      isValid = false;
    } else {
      formData.paymentPlans.forEach((plan, index) => {
        if (!plan.paymentplan_id) {
          toast.error(`Payment Plan ${index + 1} is required.`);
          isValid = false;
        }
      });
    }

    // VALIDATION FOR GALLERY: MINIMUM 2 REQUIRED
    if (!formData.gallery || formData.gallery.length < 2) {
      toast.error("Please upload at least 2 images for the gallery.");
      isValid = false;
    }

    // Check incentive fields ONLY if 'hasIncentive' radio was set to 'Yes'
    if (hasIncentive) {
      if (!formData.incentive || !formData.incentiveType || !formData.incentiveStartDate || !formData.incentiveEndDate) {
        toast.error("Please fill in all required incentive details when 'Has Incentive' is 'Yes'.");
        isValid = false;
      }
      // Optional: Add validation for incentive value based on type
      if (formData.incentiveType === 'percentage' && (isNaN(parseFloat(formData.incentive)) || parseFloat(formData.incentive) <= 0 || parseFloat(formData.incentive) > 100)) {
        toast.error("Incentive Percentage must be a number between 0 and 100.");
        isValid = false;
      }
      if (formData.incentiveType === 'amount' && (isNaN(parseFloat(formData.incentive)) || parseFloat(formData.incentive) <= 0)) {
        toast.error("Incentive Amount must be a positive number.");
        isValid = false;
      }
      // Optional: Check if end date is after start date
      if (formData.incentiveStartDate && formData.incentiveEndDate && formData.incentiveEndDate < formData.incentiveStartDate) {
        toast.error("Incentive End Date cannot be before the Start Date.");
        isValid = false;
      }
    }

    // Check bank details if applicable
    if (formData.isLaunch) {
      if (!formData.nameOfAccount?.trim() || !formData.bankName?.trim() || !formData.iban?.trim()) {
        toast.error("Please fill in all Bank Details when 'Is this a New Launch?' is selected.");
        isValid = false;
      }
    }

    if (!isValid) {
      setLoading(false);
      return;
    }

    // --- Prepare data for API ---
    const baseData = {
      name: formData.name.trim(),
      picture: formData.picture,
      masterplan: formData.masterplan,
      startingPrice: parseInt(String(formData.startingPrice).replace(/,/g, ""), 10) || 0,
      location_id: formData.location_id,
      developer_id: formData.developer_id || null,
      paymentPlans: formData.paymentPlans, // Send array of payment plans
      projectCommission: formData.projectCommission,
      deliveryDate: formData.deliveryDate,
      isLaunch: formData.isLaunch,
      isSponsored: formData.isSponsored,
      gallery: formData.gallery,
    };

    // Conditionally add incentive data ONLY if hasIncentive is true
    const incentiveData = hasIncentive ? {
      incentive: formData.incentive.trim(), // Trim incentive value as well
      incentiveType: formData.incentiveType,
      incentiveStartDate: formData.incentiveStartDate,
      incentiveEndDate: formData.incentiveEndDate,
    } : {};

    // Conditionally add bank data ONLY if isLaunch is true
    const bankData = formData.isLaunch ? {
      nameOfAccount: formData.nameOfAccount.trim(),
      bankName: formData.bankName.trim(),
      iban: formData.iban.trim(),
    } : {};


    // Combine all parts into the final payload
    const dataToSend = {
      ...baseData,
      ...incentiveData,
      ...bankData,
    };

    // Optional: Remove developer_id entirely if user is not admin and backend expects it absent
    if (user?.type !== 'admin') {
      // delete dataToSend.developer_id; // Uncomment if backend requires the field to be missing for non-admins
    }

    // --- API Call (Keep existing try/catch/finally block) ---
    try {
      console.log("Data being sent:", dataToSend); // Log the final data payload for debugging
      const response = await api.post('/projects', dataToSend);
      if (response.data.status) {
        toast.success(user?.type === "admin" || user?.autoApprove ? "Project added successfully!" : "Project added! Waiting for approval.");
        navigate('/projects');
      } else {
        toast.error(response.data.message || 'Failed to add project.');
      }
    } catch (error) {
      console.error('Error adding project:', error);
      console.error("Error Response:", error.response); // Log the detailed error response
      if (error.response) {
        if (error.response.status === 422 && error.response.data?.response?.errors) {
          const errors = error.response.data.response.errors;
          const errorMessages = Object.values(errors).flat().join('\n');
          toast.error(<>Validation Failed:<br /><pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{errorMessages}</pre></>, { autoClose: 7000 });
        } else if (error.response.data?.message) {
          toast.error(`Error: ${error.response.data.message}`);
        } else {
          toast.error(`An error occurred (Status: ${error.response.status}).`);
        }
      } else if (error.request) {
        toast.error("Network Error: Could not connect to the server.");
      } else {
        toast.error("An unexpected error occurred while submitting the form.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Add new function to handle payment plan changes
  const handlePaymentPlanChange = (index, field, value) => {
    setFormData(prev => {
      const newPaymentPlans = [...prev.paymentPlans];
      newPaymentPlans[index] = {
        ...newPaymentPlans[index],
        [field]: value
      };
      return {
        ...prev,
        paymentPlans: newPaymentPlans
      };
    });
  };

  // Add function to add new payment plan
  const addPaymentPlan = () => {
    if (formData.paymentPlans.length >= 4) {
      toast.warning("Maximum of 4 payment plans allowed per project.");
      return;
    }
    setFormData(prev => ({
      ...prev,
      paymentPlans: [...prev.paymentPlans, { paymentplan_id: '', offerPaymentplan_id: null }]
    }));
  };

  // Add function to remove payment plan
  const removePaymentPlan = (index) => {
    setFormData(prev => ({
      ...prev,
      paymentPlans: prev.paymentPlans.filter((_, i) => i !== index)
    }));
  };

  // --- Render ---
  return (
    // ... (Keep the entire JSX structure exactly the same as before, only gallery label/text changed below) ...
    <div className="card h-100 p-0 radius-12">
      <div className="card-body p-24">
        <div className="row justify-content-center">
          <div className="col-xxl-6 col-xl-8 col-lg-10">
            <div className="card border">
              <div className="card-body">
                {/* Header */}
                <div className="text-center mb-4">
                  <h4 className="fw-bolder">Add New Project</h4>
                  <p className="fs-14 text-danger-emphasis fw-semibold lh-base mt-2">
                    Ensure Payment Plans are added via the sidebar before creating a project. <br />
                    All fields marked with <span className="text-danger-600">*</span> are required.
                  </p>
                </div>

                <form onSubmit={handleSubmit} noValidate>
                  {/* --- Basic Project Details --- */}
                  <Row>
                    <Col md={user?.type === 'admin' ? 6 : 12}>
                      {/* Project Name */}
                      <div className="mb-20">
                        <label htmlFor="name" className="form-label fw-semibold text-primary-light text-sm mb-8"> Project Name <span className="text-danger-600">*</span> </label>
                        <input type="text" className="form-control radius-8" id="name" name="name" placeholder="Enter project name" value={formData.name} onChange={handleChange} required />
                      </div>
                    </Col>
                    {user?.type === "admin" && (
                      <Col md={6}>
                        {/* Developer Select */}
                        <div className="mb-20">
                          <label htmlFor="developer_id" className="form-label fw-semibold text-primary-light text-sm mb-8"> Developer <span className="text-danger-600">*</span> </label>
                          <select id="developer_id" name="developer_id" className="form-select radius-8" value={formData.developer_id} onChange={handleChange} required >
                            <option value="">Select a Developer</option>
                            {developers.map((dev) => (<option key={dev.id} value={dev.id}> {dev.name} </option>))}
                          </select>
                        </div>
                      </Col>
                    )}
                  </Row>
                  <Row>
                    {/* Project Commission */}
                    <Col md={6}>
                      <div className="mb-20"> <label htmlFor="projectCommission" className="form-label fw-semibold text-primary-light text-sm mb-8"> Project Commission (%) <span className="text-danger-600">*</span> </label> <input type="number" className="form-control radius-8" id="projectCommission" name="projectCommission" placeholder="Enter percentage (e.g., 5)" value={formData.projectCommission} onChange={handleChange} required min="0" max="100" step="0.1" /> </div>
                    </Col>
                    {/* Starting Price */}
                    <Col md={6}>
                      <div className="mb-20"> <label htmlFor="startingPrice" className="form-label fw-semibold text-primary-light text-sm mb-8" > Starting Price (EGP) <span className="text-danger-600">*</span> </label> <input type="text" className="form-control radius-8" id="startingPrice" name="startingPrice" placeholder="e.g., 1,500,000" value={formData.startingPrice} onChange={handleStartingPriceChange} required inputMode="numeric" /> </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      {/* Delivery Date */}
                      <div className="mb-20"> <label htmlFor="deliveryDate" className="form-label fw-semibold text-primary-light text-sm mb-8"> Expected Delivery Date <span className="text-danger-600">*</span> </label> <input type="date" className="form-control radius-8" id="deliveryDate" name="deliveryDate" value={formData.deliveryDate} onChange={handleChange} required /> </div>
                    </Col>
                  </Row>

                  {/* Payment Plans Section - Moved here and made full width */}
                  <div className="mb-20">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <label className="form-label fw-semibold text-primary-light text-sm mb-0">
                        Payment Plans <span className="text-danger-600">*</span>
                        <span className="text-muted ms-2">(Max 4)</span>
                      </label>
                      <Button 
                        variant="outline-primary" 
                        size="sm" 
                        onClick={addPaymentPlan}
                        className="radius-8"
                        disabled={formData.paymentPlans.length >= 4}
                      >
                        Add Payment Plan
                      </Button>
                    </div>
                    <p className="text-muted text-sm mb-3">Ensure Payment Plans are added via the sidebar before creating a project.</p>
                    
                    {formData.paymentPlans.map((plan, index) => (
                      <div key={index} className="border rounded p-3 mb-3 position-relative">
                        {index > 0 && (
                          <Button
                            variant="outline-danger"
                            size="sm"
                            className="position-absolute top-0 end-0 m-2"
                            onClick={() => removePaymentPlan(index)}
                          >
                            <i className="bi bi-x me-1"></i>
                            Delete Payment Plan
                          </Button>
                        )}
                        <Row>
                          <Col md={12}>
                            <div className="mb-3">
                              <label className="form-label fw-semibold text-primary-light text-sm mb-2">
                                Payment Plan {index + 1} <span className="text-danger-600">*</span>
                              </label>
                              <select
                                className="form-select radius-8"
                                value={plan.paymentplan_id}
                                onChange={(e) => handlePaymentPlanChange(index, 'paymentplan_id', e.target.value)}
                                required
                              >
                                <option value="">Select Payment Plan</option>
                                {paymentplans.map((pp) => (
                                  <option key={pp.id} value={pp.id}>
                                    {pp.name}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </Col>
                        </Row>
                      </div>
                    ))}
                  </div>

                  <Row>
                    {/* Location Select */}
                    <Col md={6}>
                      <div className="mb-20">
                        <label htmlFor="location_id" className="form-label fw-semibold text-primary-light text-sm mb-8"> Location <span className="text-danger-600">*</span> </label>
                        <Select
                          inputId="location_id"
                          name="location_id"
                          options={locationOptions}
                          value={locationOptions.find(option => option.value === formData.location_id)}
                          onChange={handleSelectChange}
                          placeholder="Search or select a location..."
                          isClearable
                          required
                          styles={{
                            control: (baseStyles, state) => ({ ...baseStyles, borderColor: state.isFocused ? '#86b7fe' : '#dee2e6', boxShadow: state.isFocused ? '0 0 0 0.25rem rgba(13, 110, 253, 0.25)' : 'none', borderRadius: '0.5rem', minHeight: '38px', }),
                            input: (baseStyles) => ({ ...baseStyles, margin: '0px', padding: '0px' }),
                            valueContainer: (baseStyles) => ({ ...baseStyles, padding: '0px 8px' }),
                            placeholder: (baseStyles) => ({ ...baseStyles, color: '#6c757d', }),
                          }}
                        />
                      </div>
                    </Col>
                  </Row>


                  {/* --- Incentive Section --- */}
                  {/* This is the crucial part for the UI */}
                  <div className="mb-20">
                    <label className="form-label fw-semibold text-primary-light text-sm mb-8"> Has Incentive? <span className="text-danger-600">*</span> </label>
                    {/* Radio buttons control the 'hasIncentive' state */}
                    <div className="d-flex align-items-center gap-4">
                      {/* Use 'value="yes"' and 'value="no"' for clarity in the handler */}
                      <Form.Check inline type="radio" label="Yes" name="hasIncentive" id="hasIncentiveYes" value="yes" checked={hasIncentive === true} onChange={handleHasIncentiveChange} required />
                      <Form.Check inline type="radio" label="No" name="hasIncentive" id="hasIncentiveNo" value="no" checked={hasIncentive === false} onChange={handleHasIncentiveChange} required />
                    </div>
                  </div>
                  {/* This block is only rendered if hasIncentive state is true */}
                  {hasIncentive && (
                    <div className="p-3 mb-3 border rounded border-primary-light bg-primary-50">
                      <h6 className="text-primary-dark fw-semibold mb-3">Incentive Details</h6>
                      {/* Incentive Value */}
                      <div className="mb-20">
                        <label htmlFor="incentive" className="form-label fw-semibold text-primary-light text-sm mb-8"> Incentive Value <span className="text-danger-600">*</span> </label>
                        <input type="text" className="form-control radius-8" id="incentive" name="incentive" placeholder={formData.incentiveType === 'percentage' ? 'Enter percentage (e.g., 5)' : 'Enter amount (e.g., 10000)'} value={formData.incentive} onChange={handleChange} required={hasIncentive} inputMode="decimal" />
                      </div>
                      {/* Incentive Type */}
                      <div className="mb-20">
                        <label className="form-label fw-semibold text-primary-light text-sm mb-8"> Incentive Type <span className="text-danger-600">*</span> </label>
                        <div className="d-flex align-items-center gap-4">
                          {/* These use the generic handleChange */}
                          <Form.Check inline type="radio" label="Percentage (%)" name="incentiveType" id="incentiveTypePercentage" value="percentage" checked={formData.incentiveType === "percentage"} onChange={handleChange} required={hasIncentive} />
                          <Form.Check inline type="radio" label="Fixed Amount (EGP)" name="incentiveType" id="incentiveTypeAmount" value="amount" checked={formData.incentiveType === "amount"} onChange={handleChange} required={hasIncentive} />
                        </div>
                      </div>
                      {/* Incentive Start Date */}
                      <div className="mb-20">
                        <label htmlFor="incentiveStartDate" className="form-label fw-semibold text-primary-light text-sm mb-8"> Incentive Start Date <span className="text-danger-600">*</span> </label>
                        <input type="date" className="form-control radius-8" id="incentiveStartDate" name="incentiveStartDate" value={formData.incentiveStartDate} onChange={handleChange} required={hasIncentive} />
                      </div>
                      {/* Incentive End Date */}
                      <div className="mb-20">
                        <label htmlFor="incentiveEndDate" className="form-label fw-semibold text-primary-light text-sm mb-8"> Incentive End Date <span className="text-danger-600">*</span> </label>
                        <input type="date" className="form-control radius-8" id="incentiveEndDate" name="incentiveEndDate" value={formData.incentiveEndDate} onChange={handleChange} required={hasIncentive} />
                      </div>
                    </div>
                  )}


                  {/* --- Images Section --- */}
                  <Row>
                    <Col md={6}> {/* Cover Photo */} <div className="mb-20"> <label htmlFor="picture" className="form-label fw-semibold text-primary-light text-sm mb-8"> Cover Photo <span className="text-muted fw-normal">(Max 2MB, JPG/PNG)</span><span className="text-danger-600">*</span> </label> <input type="file" className="form-control radius-8" id="picture" name="picture" onChange={(e) => handleFileChange(e, 'picture')} accept="image/jpeg, image/png, image/jpg" required /> {imagePreviewUrl && (<div className="mt-2"> <Image src={imagePreviewUrl} alt="Cover Preview" thumbnail style={{ maxHeight: '100px' }} /> </div>)} </div> </Col>
                    <Col md={6}> {/* Masterplan */} <div className="mb-20"> <label htmlFor="masterplan" className="form-label fw-semibold text-primary-light text-sm mb-8"> Masterplan Image <span className="text-muted fw-normal">(Max 2MB, JPG/PNG)</span><span className="text-danger-600">*</span> </label> <input type="file" className="form-control radius-8" id="masterplan" name="masterplan" onChange={(e) => handleFileChange(e, 'masterplan')} accept="image/jpeg, image/png, image/jpg" required /> {masterplanPreviewUrl && (<div className="mt-2"> <Image src={masterplanPreviewUrl} alt="Masterplan Preview" thumbnail style={{ maxHeight: '100px' }} /> </div>)} </div> </Col>
                  </Row>
                  {/* Gallery Input & Preview */}
                  {/* *** MODIFIED: Gallery Section with Max 6 Limit Indication *** */}
                  <div className="mb-20">
                    <label htmlFor="gallery" className="form-label fw-semibold text-primary-light text-sm mb-8">
                      Gallery Images (Landscape Mode)
                      {/* *** UPDATED TEXT *** */}
                      <span className="text-muted fw-normal">
                        (Min 2, Max 6 files, Max 2MB each, JPG/PNG)
                      </span>
                      <span className="text-danger-600">*</span>
                    </label>
                    <input
                      type="file"
                      className="form-control radius-8"
                      id="gallery"
                      name="gallery"
                      multiple
                      onChange={(e) => handleMultipleFilesChange(e, 'gallery')} // Logic updated inside this function
                      accept="image/jpeg, image/png, image/jpg"
                    />
                    {/* Image Previews */}
                    {formData.gallery && formData.gallery.length > 0 && (
                      <div className="mt-3 d-flex flex-wrap gap-2 border p-2 rounded">
                        {formData.gallery.map((imgBase64, index) => (
                          <div key={index} className="position-relative">
                            <Image src={imgBase64} alt={`Gallery image ${index + 1}`} thumbnail style={{ height: '80px', width: 'auto' }} />
                            <CloseButton
                              className="position-absolute top-0 end-0 bg-light rounded-circle p-1"
                              style={{ transform: 'translate(30%, -30%)', '--bs-btn-close-focus-shadow': 'none' }}
                              title="Remove Image"
                              onClick={() => handleRemoveGalleryImage(index)}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Image Count and Limit Info */}
                    <p className="text-muted text-sm mt-1 mb-0">
                      {formData.gallery.length} image(s) selected. (Minimum 2, Maximum 6 required)
                    </p>
                  </div>


                  {/* --- Launch & Sponsor Section --- */}
                  <div className="mb-20"> <Form.Check type="checkbox" id="isLaunch" name="isLaunch" label="Is this a New Launch? Select if YES" checked={formData.isLaunch} onChange={handleChange} /> </div>

                  {/* --- Conditional Bank Details Section --- */}
                  {formData.isLaunch && (<div className="p-3 my-3 border rounded border-info-light bg-info-50"> <h6 className="text-info-dark fw-semibold mb-3">Bank Details (Required for New Launch)</h6> <Row> <Col md={6}> <div className="mb-20"> <label htmlFor="nameOfAccount" className="form-label fw-semibold text-primary-light text-sm mb-8"> Account Holder Name <span className="text-danger-600">*</span> </label> <input type="text" className="form-control radius-8" id="nameOfAccount" name="nameOfAccount" placeholder="Enter account holder name" value={formData.nameOfAccount} onChange={handleChange} required={formData.isLaunch} /> </div> </Col> <Col md={6}> <div className="mb-20"> <label htmlFor="bankName" className="form-label fw-semibold text-primary-light text-sm mb-8"> Bank Name <span className="text-danger-600">*</span> </label> <input type="text" className="form-control radius-8" id="bankName" name="bankName" placeholder="Enter bank name" value={formData.bankName} onChange={handleChange} required={formData.isLaunch} /> </div> </Col> </Row> <div className="mb-20"> <label htmlFor="iban" className="form-label fw-semibold text-primary-light text-sm mb-8"> IBAN <span className="text-danger-600">*</span> </label> <input type="text" className="form-control radius-8" id="iban" name="iban" placeholder="Enter IBAN" value={formData.iban} onChange={handleChange} required={formData.isLaunch} /> </div> </div>)}

                  {/* Sponsored Toggle */}
                  {user?.type === "admin" && (<div className="mb-20"> <Form.Check type="switch" id="isSponsored" name="isSponsored" label="Mark as Sponsored project?" checked={formData.isSponsored} onChange={handleChange} /> </div>)}


                  {/* Action Buttons */}
                  <div className="d-flex align-items-center justify-content-center gap-3 mt-4">
                    <button type="button" className="btn btn-outline-danger radius-8 px-5 py-2" onClick={handleCancel} disabled={loading} > Cancel </button>
                    <button type="submit" className="btn btn-primary radius-8 px-5 py-2 d-flex align-items-center justify-content-center" disabled={loading} > {loading ? (<> <Spinner animation="border" size="sm" className="me-2" /> Saving... </>) : ('Save Project')} </button>
                  </div>
                </form>
              </div> {/* End card-body */}
            </div> {/* End card */}
          </div> {/* End col */}
        </div> {/* End row */}
      </div> {/* End card-body p-24 */}
    </div> /* End card h-100 */

  );
};

export default AddProjectLayer;